import { useState, useEffect, useCallback, useRef } from 'react';

import { useDispatch } from 'react-redux';

import {

  getLeadsStatus,

  getLeadSources,

  getLeadBrands,

  getLeadById,

  getLeadInitiate,
  getLeadAssignedUsers,

} from '../../../store/slices/leadsSlice';

import { extractList } from '../../../utils/response';

import api from '../../../api/config';

import { LOOKUP } from '../../../api/endpoints';

import {

  mapLeadApiToForm,

  mapListLeadToFormPrefill,

  extractLeadAttachmentIds,

  createEmptyLeadForm,

  validateLeadForm,

} from '../leadFormHelpers';

import {

  mapStatusOptions,

  mapSourceOptions,

  mapBrandOptions,

  mapCountryOptions,

  getCachedSources,

  getCachedBrands,

  getCachedCountries,

  setCachedSources,

  setCachedBrands,

  setCachedCountries,

} from '../leadFormOptionsCache';



export function useLeadForm({

  leadId,

  isCreateMode,

  user,

  enabled = true,

  prefillLead = null,

  useInitiateEndpoint = false,

}) {

  const dispatch = useDispatch();

  const prefillAppliedRef = useRef(null);

  const optionsSessionRef = useRef(null);

  const leadFetchSessionRef = useRef(null);

  const leadFetchInFlightRef = useRef(null);



  const [formData, setFormData] = useState(() => createEmptyLeadForm(user));

  const [errors, setErrors] = useState({});

  const [statusOptions, setStatusOptions] = useState([]);

  const [loading, setLoading] = useState(false);

  const [leadLoading, setLeadLoading] = useState(false);

  const [sourceOptions, setSourceOptions] = useState([]);

  const [brandOptions, setBrandOptions] = useState([]);

  const [countryOptions, setCountryOptions] = useState([]);

  const [optionsLoaded, setOptionsLoaded] = useState(false);

  const [attachmentsHydrate, setAttachmentsHydrate] = useState(0);



  const applyLeadToForm = useCallback((leadData, assigneeIds = []) => {

    const ids =

      assigneeIds.length > 0

        ? assigneeIds

        : Array.isArray(leadData?.assignees)

          ? leadData.assignees.map((a) => a.id ?? a.uuid).filter(Boolean)

          : [];



    setFormData({

      ...mapLeadApiToForm(leadData),

      assignee_ids: ids.length ? [ids[0]] : [],

      attachment_ids: extractLeadAttachmentIds(leadData),

      attachments: Array.isArray(leadData.attachments) ? leadData.attachments : [],

    });

    setAttachmentsHydrate((x) => x + 1);

  }, []);



  const loadOptions = useCallback(async () => {

    const cachedSources = getCachedSources() ?? [];

    const cachedBrands = getCachedBrands() ?? [];

    const cachedCountries = getCachedCountries();



    if (cachedSources.length) setSourceOptions(cachedSources);

    if (cachedBrands.length) setBrandOptions(cachedBrands);

    if (cachedCountries?.length) setCountryOptions(cachedCountries);



    const fetches = [];



    fetches.push(

      dispatch(getLeadsStatus({ 'conditions[model]': 'Lead' }))

        .unwrap()

        .then((res) => setStatusOptions(mapStatusOptions(extractList(res))))

        .catch(() => {})

    );



    if (!cachedSources.length) {

      fetches.push(

        dispatch(getLeadSources())

          .unwrap()

          .then((res) => {

            const options = mapSourceOptions(extractList(res));

            setCachedSources(options);

            setSourceOptions(options);

          })

          .catch(() => {})

      );

    }



    if (!cachedBrands.length) {

      fetches.push(

        dispatch(getLeadBrands())

          .unwrap()

          .then((res) => {

            const options = mapBrandOptions(extractList(res));

            setCachedBrands(options);

            setBrandOptions(options);

          })

          .catch(() => {})

      );

    }



    if (!cachedCountries?.length) {

      fetches.push(

        api

          .get(LOOKUP.COUNTRIES)

          .then((res) => {

            const raw =

              res?.data?.data?.list ?? res?.data?.list ?? extractList(res?.data);

            const options = mapCountryOptions(raw);

            setCachedCountries(options);

            setCountryOptions(options);

          })

          .catch(() => {})

      );

    }



    await Promise.all(fetches);

    setOptionsLoaded(true);

  }, [dispatch]);



  const fetchLeadData = useCallback(async () => {

    if (!leadId) return;

    if (leadFetchInFlightRef.current === leadId) return;



    leadFetchInFlightRef.current = leadId;

    setLeadLoading(true);



    try {

      const response = await dispatch(
        useInitiateEndpoint ? getLeadInitiate(leadId) : getLeadById(leadId)
      ).unwrap();

      if (!response?.success) return;



      const leadData = response.data ?? response;

      let assigneeIds = Array.isArray(leadData?.assignees)

        ? leadData.assignees.map((a) => a.id ?? a.uuid).filter(Boolean)

        : [];



      if (!assigneeIds.length) {

        try {

          const assignedRes = await dispatch(getLeadAssignedUsers(leadId)).unwrap();

          const logs =

            assignedRes?.data?.data?.status_logs ??

            assignedRes?.data?.status_logs ??

            assignedRes?.status_logs ??

            [];

          const ids = [

            ...new Set(

              logs.map((s) => s.assignee?.id ?? s.assignee?.uuid ?? s.user_id).filter(Boolean)

            ),

          ];

          if (ids.length) assigneeIds = ids;

        } catch (_) {

          // keep assignees from lead response

        }

      }



      applyLeadToForm(leadData, assigneeIds);

      leadFetchSessionRef.current = leadId;

    } catch (err) {

      console.error('Error fetching lead data:', err);

      leadFetchSessionRef.current = null;

    } finally {

      if (leadFetchInFlightRef.current === leadId) {

        leadFetchInFlightRef.current = null;

      }

      setLeadLoading(false);

    }

  }, [dispatch, leadId, applyLeadToForm]);



  useEffect(() => {

    if (!enabled) {

      optionsSessionRef.current = null;

      leadFetchSessionRef.current = null;

      leadFetchInFlightRef.current = null;

      return;

    }



    if (!isCreateMode && prefillLead?.id && prefillAppliedRef.current !== prefillLead.id) {

      const prefill = mapListLeadToFormPrefill(prefillLead);

      if (prefill) {

        setFormData((prev) => ({ ...prev, ...prefill }));

        prefillAppliedRef.current = prefillLead.id;

      }

    }



    let cancelled = false;



    const init = async () => {

      setLoading(true);

      try {

        const tasks = [];



        if (optionsSessionRef.current !== 'loaded') {

          optionsSessionRef.current = 'loading';

          tasks.push(

            loadOptions().then(() => {

              if (!cancelled) optionsSessionRef.current = 'loaded';

            })

          );

        }



        if (!isCreateMode && leadId && leadFetchSessionRef.current !== leadId) {

          tasks.push(fetchLeadData());

        }



        await Promise.all(tasks);

      } finally {

        if (!cancelled) setLoading(false);

      }

    };



    init();



    return () => {

      cancelled = true;

    };

  }, [enabled, leadId, isCreateMode, prefillLead?.id, loadOptions, fetchLeadData]);



  useEffect(() => {

    if (!enabled || !optionsLoaded || !isCreateMode) return;

    const pendingStatus = statusOptions.find((s) => (s.name || '').toLowerCase() === 'pending');

    const reviveBrand = brandOptions.find(

      (b) =>

        (b.name || '').toLowerCase().replace(/\s/g, '') === 'revivepay' ||

        (b.name || '').toLowerCase().includes('revive pay')

    );

    if (pendingStatus || reviveBrand) {

      setFormData((prev) => ({

        ...prev,

        ...(pendingStatus && { status_id: pendingStatus.id }),

        ...(reviveBrand && { brand_id: reviveBrand.id }),

      }));

    }

  }, [enabled, optionsLoaded, isCreateMode, statusOptions, brandOptions]);



  const handleChange = (field) => (event) => {

    const value = event.target.value;

    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));

  };



  const handlePhoneChange = (e) => {

    let value = e.target.value.replace(/[^0-9]/g, '');

    if (value.length > 10) value = value.slice(0, 10);

    setFormData((prev) => ({ ...prev, phone: value }));

    if (errors.phone) setErrors((prev) => ({ ...prev, phone: '' }));

  };



  const runValidation = () => {

    const newErrors = validateLeadForm(formData, statusOptions);

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;

  };



  const resetForm = () => {

    prefillAppliedRef.current = null;

    optionsSessionRef.current = null;

    leadFetchSessionRef.current = null;

    leadFetchInFlightRef.current = null;

    setFormData(createEmptyLeadForm(user));

    setErrors({});

    setAttachmentsHydrate(0);

    setOptionsLoaded(false);

  };



  return {

    formData,

    setFormData,

    errors,

    setErrors,

    loading,

    leadLoading,

    statusOptions,

    sourceOptions,

    brandOptions,

    countryOptions,

    attachmentsHydrate,

    handleChange,

    handlePhoneChange,

    runValidation,

    resetForm,

    isDisabled: loading || leadLoading,

  };

}

